import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Eye, EyeOff, Lock, User } from 'lucide-react';
import Button from './ui/Button';
import { supabase } from '../supabaseClient';
import { toast } from 'sonner';
import { jwtDecode } from 'jwt-decode';
import { Role } from '../hooks/useRBAC';

interface AdminLoginProps {
	onLogin: (isAdmin: boolean) => void;
}

interface JWTPayload {
	user_role?: Role;
	[key: string]: any;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLogin }) => {
	const navigate = useNavigate();
	const [credentials, setCredentials] = useState({
		email: '', // Changed from username to email
		password: '',
	});
	const [showPassword, setShowPassword] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState('');
	

	// Function to extract role from JWT token
	const extractRoleFromToken = (token: string): Role => {
		try {
			const decoded = jwtDecode<JWTPayload>(token);
			return decoded.user_role || null;
		} catch (_error) {
			console.error('Error extracting role from token:', _error);
			// Silently handle error and return null
			return null;
		}
	};

	

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setIsLoading(true);
		setError('');

		try {
			const { data, error } = await supabase.auth.signInWithPassword({
				email: credentials.email,
				password: credentials.password,
			});

			if (error) {
				setError(error.message);
				toast.error('Login failed: ' + error.message);
				return;
			}

			// Check if user has a role in their JWT token
			if (data.session?.access_token) {
				const userRole = extractRoleFromToken(data.session.access_token);

				if (!userRole) {
					// No role found in token - sign out and show error
					await supabase.auth.signOut();
					setError('Access denied: Your account does not have the required permissions.');
					toast.error('Access denied: Your account does not have the required permissions.');
					return;
				}

				// User has a role, proceed with login
				if (data.user) {
					await supabase
						.from('profiles')
						.update({ last_login: new Date().toISOString() })
						.eq('id', data.user.id);
				}

				onLogin(true);
				navigate('/dashboard');
				toast.success('Login successful!');
			} else {
				// No session or access token available
				setError('Authentication error: No session established');
				toast.error('Authentication error: No session established');
			}
		} catch (err) {
			setError('An unexpected error occurred');
			toast.error('An unexpected error occurred');
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="min-h-screen bg-slate-100 flex items-center justify-center p-2">
			<div className="w-full max-w-lg">
				<div className="bg-white rounded-2xl shadow-xl p-5 py-8">
					{/* Header */}
					<div className="text-center mb-3">
						<div className="w-6 h-6 bg-brand-orange rounded-full flex items-center justify-center mx-auto mb-1">
							<Shield className="w-5 h-5 text-white" />
						</div>
						<h1 className="text-xl font-bold text-gray-900">Admin Login</h1>
						<p className="text-gray-600 mt-1 text-body">Access the verification dashboard</p>
					</div>

					{/* Form */}
					<form onSubmit={handleSubmit} className="space-y-5 p-2">
						{error && (
							<div className="bg-red-50 border border-red-200 rounded-lg p-3">
								<p className="text-red-600 text-sm">{error}</p>
							</div>
						)}

						<div>
							<label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
							<div className="relative">
								<User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
								<input
									type="email"
									value={credentials.email}
									onChange={e => setCredentials(prev => ({ ...prev, email: e.target.value }))}
									className="w-full pl-10 pr-4 py-3 text-body border border-gray-300 rounded-lg focus:ring-2 focus:ring-ghana-red focus:border-transparent transition-colors"
									placeholder="Enter email"
									required
								/>
							</div>
						</div>

						<div>
							<label className="block text-xs font-medium text-gray-700 mb-1">Password</label>
							<div className="relative">
								<Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
								<input
									type={showPassword ? 'text' : 'password'}
									value={credentials.password}
									onChange={e => setCredentials(prev => ({ ...prev, password: e.target.value }))}
									className="w-full pl-10 text-body pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-ghana-red focus:border-transparent transition-colors"
									placeholder="Enter password"
									required
								/>
								<button
									type="button"
									onClick={() => setShowPassword(!showPassword)}
									className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
								>
									{showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
								</button>
							</div>
						</div>

					
						<div className="text-right">
							<button
								type="button"
								className="text-xs text-ghana-red hover:underline cursor-pointer"
								onClick={() => navigate('/send-reset-link')}
							>
								Forgot password?
							</button>
						</div>

						<Button
							type="submit"
							disabled={isLoading}
							className="w-full text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isLoading ? 'Signing in...' : 'Sign In'}
						</Button>
					</form>

					{/* Back to Home */}
					<div className="mt-2 text-center">
						<button
							onClick={() => navigate('/')}
							className="text-ghana-red hover:text-red-700 text-sm font-medium"
						>
							← Back to Home
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AdminLogin;
